import React, { useState } from "react";
import { Controller, Control, FieldValues } from "react-hook-form";
import { TextField, Input, Label } from "react-aria-components";
import { LockClosedIcon } from "@heroicons/react/24/outline";
import { PatternFormat } from "react-number-format";

import FieldError from "components/Field/FieldError";
import { isValidRoutingNumber } from "helpers/validation";

type BankAccountFieldsProps = {
  control: Control<FieldValues>;
};

const BankAccountFields: React.FC<BankAccountFieldsProps> = ({ control }) => {
  const [holderNameFocused, setHolderNameFocused] = useState(false);
  const [routingNumberFocused, setRoutingNumberFocused] = useState(false);
  const [accountNumberFocused, setAccountNumberFocused] = useState(false);

  return (
    <div className="mb-4">
      <div className="mt-6 flex flex-col space-y-4">
        {/* Bank Name (optional) */}
        <Controller
          control={control}
          name="bank_name"
          render={({
            field: { name, value, onChange, onBlur, ref },
            fieldState: { invalid },
          }) => {
            return (
              <TextField name={name} value={value || ""} isInvalid={invalid}>
                <Label className="-mb-1 block font-semibold text-evergreen">
                  Bank Name{" "}
                  <span className="font-normal text-gray-500">(optional)</span>
                </Label>
                <Input
                  ref={ref}
                  type="text"
                  onChange={onChange}
                  onBlur={onBlur}
                  placeholder="e.g. Chase, Wells Fargo"
                  className="block w-full rounded-none border-b border-gray-400 bg-transparent py-2 text-2xl font-bold text-soil transition-colors placeholder:text-soil placeholder:text-opacity-25 focus:border-cantelope focus:shadow-focus focus:outline-none"
                />
              </TextField>
            );
          }}
        />

        {/* Account Holder Name */}
        <Controller
          control={control}
          name="bank_account_holder_full_name"
          rules={{
            required: "Account holder name is required.",
          }}
          render={({
            field: { name, value, onChange, onBlur, ref },
            fieldState: { invalid, error, isDirty, isTouched },
          }) => (
            <TextField
              name={name}
              value={value || ""}
              isRequired
              isInvalid={invalid}
            >
              <Label className="-mb-1 block font-semibold text-evergreen">
                Account Holder Name
              </Label>
              <Input
                ref={ref}
                type="text"
                onChange={onChange}
                onFocus={() => setHolderNameFocused(true)}
                onBlur={() => {
                  onBlur();
                  setHolderNameFocused(false);
                }}
                className="block w-full rounded-none border-b border-gray-400 bg-transparent py-2 text-2xl font-bold text-soil transition-colors placeholder:text-soil placeholder:text-opacity-25 focus:border-cantelope focus:shadow-focus focus:outline-none"
              />
              {error?.message && isDirty && isTouched && !holderNameFocused && (
                <FieldError>{String(error.message)}</FieldError>
              )}
            </TextField>
          )}
        />

        {/* Account Type */}
        <Controller
          control={control}
          name="bank_account_type"
          rules={{
            required: "Account type is required.",
          }}
          render={({
            field: { value, onChange },
            fieldState: { error, isDirty, isTouched },
          }) => {
            const handleKeyDown = (
              e: React.KeyboardEvent,
              currentValue: "Checking" | "Savings",
            ) => {
              const nextValue =
                currentValue === "Checking" ? "Savings" : "Checking";

              if (
                e.key === "ArrowRight" ||
                e.key === "ArrowDown" ||
                e.key === "ArrowLeft" ||
                e.key === "ArrowUp"
              ) {
                e.preventDefault();
                onChange(nextValue);
              } else if (e.key === " " || e.key === "Enter") {
                e.preventDefault();
                onChange(currentValue);
              }
            };

            const isCheckingSelected = value === "Checking";
            const isSavingsSelected = value === "Savings";

            return (
              <div>
                <Label
                  id="bank-account-type-label"
                  className="-mb-1 block font-semibold text-evergreen"
                >
                  Account Type
                </Label>
                <div
                  role="radiogroup"
                  aria-labelledby="bank-account-type-label"
                  className="mt-2 flex space-x-3"
                >
                  <button
                    type="button"
                    role="radio"
                    aria-checked={isCheckingSelected}
                    tabIndex={isCheckingSelected || !value ? 0 : -1}
                    onClick={() => onChange("Checking")}
                    onKeyDown={(e) => handleKeyDown(e, "Checking")}
                    className={`rounded px-4 py-2 transition-colors ${
                      isCheckingSelected
                        ? "bg-cantelope text-soil"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    Checking
                  </button>
                  <button
                    type="button"
                    role="radio"
                    aria-checked={isSavingsSelected}
                    tabIndex={isSavingsSelected ? 0 : -1}
                    onClick={() => onChange("Savings")}
                    onKeyDown={(e) => handleKeyDown(e, "Savings")}
                    className={`rounded px-4 py-2 transition-colors ${
                      isSavingsSelected
                        ? "bg-cantelope text-soil"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    Savings
                  </button>
                </div>
                {error?.message && isDirty && isTouched && (
                  <FieldError>{String(error.message)}</FieldError>
                )}
              </div>
            );
          }}
        />

        {/* Routing Number and Account Number row */}
        <div className="flex flex-col space-y-4 sm:flex-row sm:space-x-4 sm:space-y-0">
          {/* Routing Number */}
          <Controller
            control={control}
            name="bank_routing_number"
            rules={{
              required: "Routing number is required.",
              validate: {
                length: (value) =>
                  value && value.length === 9
                    ? true
                    : "Routing number must be 9 digits.",
                validABA: (value) =>
                  isValidRoutingNumber(value)
                    ? true
                    : "Invalid routing number.",
              },
            }}
            render={({
              field: { name, value, onChange, onBlur, ref },
              fieldState: { invalid, error, isDirty, isTouched },
            }) => (
              <TextField
                name={name}
                value={value || ""}
                inputMode="numeric"
                isRequired
                isInvalid={invalid}
                className="flex-1"
              >
                <Label className="-mb-1 block font-semibold text-evergreen">
                  Routing Number
                </Label>
                <PatternFormat
                  value={value || ""}
                  onValueChange={(values) => {
                    const { value: unformattedValue } = values;
                    onChange(unformattedValue);
                  }}
                  format="#########"
                  inputMode="numeric"
                  onBlur={onBlur}
                  customInput={Input}
                  getInputRef={(el: any) => {
                    ref(el);
                  }}
                  onFocus={() => setRoutingNumberFocused(true)}
                  onBlurCapture={() => setRoutingNumberFocused(false)}
                  placeholder="123456789"
                  className="block w-full rounded-none border-b border-gray-400 bg-transparent py-2 text-2xl font-bold text-soil transition-colors placeholder:text-soil placeholder:text-opacity-25 focus:border-cantelope focus:shadow-focus focus:outline-none"
                />
                {error?.message &&
                  isDirty &&
                  isTouched &&
                  !routingNumberFocused && (
                    <FieldError>{String(error.message)}</FieldError>
                  )}
              </TextField>
            )}
          />

          {/* Account Number */}
          <Controller
            control={control}
            name="bank_account_number"
            rules={{
              required: "Account number is required.",
              validate: {
                length: (value) =>
                  value && value.length >= 4 && value.length <= 17
                    ? true
                    : "Account number must be between 4 and 17 digits.",
              },
            }}
            render={({
              field: { name, value, onChange, onBlur, ref },
              fieldState: { invalid, error, isDirty, isTouched },
            }) => (
              <TextField
                name={name}
                value={value || ""}
                inputMode="numeric"
                isRequired
                isInvalid={invalid}
                className="flex-1"
              >
                <Label className="-mb-1 block font-semibold text-evergreen">
                  Account Number
                </Label>
                <Input
                  ref={ref}
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  onChange={(e) => {
                    // Only allow numeric input
                    const numericValue = e.target.value.replace(/\D/g, "");
                    onChange(numericValue);
                  }}
                  onFocus={() => setAccountNumberFocused(true)}
                  onBlur={() => {
                    onBlur();
                    setAccountNumberFocused(false);
                  }}
                  className="block w-full rounded-none border-b border-gray-400 bg-transparent py-2 text-2xl font-bold text-soil transition-colors placeholder:text-soil placeholder:text-opacity-25 focus:border-cantelope focus:shadow-focus focus:outline-none"
                />
                {error?.message &&
                  isDirty &&
                  isTouched &&
                  !accountNumberFocused && (
                    <FieldError>{String(error.message)}</FieldError>
                  )}
              </TextField>
            )}
          />
        </div>
      </div>

      <div className="mt-4 flex items-center space-x-2">
        <LockClosedIcon className="h-6 w-6 text-green-600" />
        <p className="text-sm text-gray-600">
          Your bank information is secure and encrypted.
        </p>
      </div>
    </div>
  );
};

export default BankAccountFields;
